<template>
  <v-layout>
    <v-dialog v-model="showDispatchDialog" lazy
              persistent=""
              @input="v => v"
              max-width="500">
      <v-card class="pa-4">
        <v-layout column>
          <span class="title mb-3">Accept</span>
          <i>Available loads: {{availableLoads}}</i>
          <v-select
            :items="loadsOptions"
            v-model.number="acceptedDispatchLoads"
            :error-messages="errors.first('acceptedDispatchLoads', 'modal')"
            data-vv-as="Accepted Loads"
            data-vv-name="acceptedDispatchLoads"
            data-vv-scope="modal"
            v-validate="`required|min_value:1|max_value:${availableLoads}`"
            label="Accepted Loads">
          </v-select>

          <v-card-actions>
            <v-spacer />
            <v-btn color="accent"
                   :loading="loadingDispatchRequests || loadingBtn"
                   :disabled="errors.any('modal')"
                   @click="acceptDispatchAssignment()">
              Accept
            </v-btn>
            <v-btn color="accent" flat
                   @click="showDispatchDialog = false">
              Cancel
            </v-btn>
          </v-card-actions>
        </v-layout>
      </v-card>
    </v-dialog>

    <v-master-detail-pane :selected="selectedLoad"
                          @reset="selectLoad(null)">

      <template slot="master">
        <v-load-nav-list v-if="dispatchRequests"
                         dispatched
                         title="Dispatch Requests"
                         searchPrompt="Search Dispatch Requests"
                         :loading="loadingDispatchRequests"
                         :accurateSince="accurateSince"
                         :loads="filteredDispatchRequests"
                         :carrierLoads="groupedByDispatcherLoads"
                         :company="company"
                         @update:searchTerm="search = $event"
                         @refresh="reloadDispatchRequests">

          <template slot="loadTile"
                    slot-scope="{ idx, load: dispatch }">

            <v-list-tile :key="`dispatched_load_${idx}_${dispatch.humanId}`"
                         @click="selectLoad(dispatch)"
                         :class="{'primary lighten-1': isSelectedLoad(dispatch)}"
                         :inactive="isSelectedLoad(dispatch)"
                         ripple avatar>

              <v-list-tile-content>
                <v-list-tile-title>{{ loadGroupOwnerName(dispatch) }}</v-list-tile-title>
                <v-list-tile-sub-title class="text--primary">
                  {{ dispatch.humanId }}: ${{ dispatch.payableAmount }} (${{ pricePerDistance(dispatch) }}/{{ siteDistanceMeasurePerLabel }})
                </v-list-tile-sub-title>
                <v-list-tile-sub-title>
                  {{ (dispatch.loadGroup.scheduledPickupTime) | formatShortDate }}
                </v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action>
                <v-chip color="primary"
                        text-color="white" small>
                  {{ dispatchRequestVisibleCount(dispatch) }}
                </v-chip>
              </v-list-tile-action>
            </v-list-tile>
            <v-divider :key="`dispatched_divider_${idx}_${dispatch.humanId}`"/>
          </template>

          <template slot="carrierLoadsTile" slot-scope="skope">
            <v-list-group :key="`groupedBy_${skope.idx}`">
              <v-list-tile slot="activator">
                <v-list-tile-action>
                  <v-chip color="primary"
                          text-color="white" small>
                    {{ loadGroupCollectionTruckCount(skope.loads) }}
                  </v-chip>
                </v-list-tile-action>
                <v-list-tile-title>
                  {{skope.carrier}}
                </v-list-tile-title>
              </v-list-tile>
              <template v-for="load in skope.loads" >
                <v-list-tile :key="`grouped_load_${load.humanId}`"
                             @click="selectLoad(load)"
                             :class="{'primary lighten-1': isSelectedLoad(load)}"
                             :inactive="isSelectedLoad(load)"
                             ripple avatar>
                  <v-list-tile-content>
                    <v-list-tile-title v-if="loadGroupCustomerCompanyName">{{ loadGroupCustomerCompanyName(load) }}</v-list-tile-title>
                    <v-list-tile-sub-title class="text--primary">
                      {{ load.humanId }}: ${{ load.payableAmount }} (${{ pricePerDistance(load) }}/{{ siteDistanceMeasurePerLabel }})
                    </v-list-tile-sub-title>
                    <v-list-tile-sub-title>
                      {{ (load.loadGroup.scheduledPickupTime) | formatShortDate }}
                    </v-list-tile-sub-title>
                  </v-list-tile-content>

                </v-list-tile>
                <v-divider :key="`load_divider_${load.humanId}`"/>
              </template>
            </v-list-group>
          </template>
        </v-load-nav-list>


      </template>
      <template slot="detail">
        <v-load-group-details dispatched
                              disableAddressView
                              :group-booked-loads="bookedContracts"
                              :planning-load="selectedLoad"
                              :company="company"
                              @reload="onRefresh()"
                              banner-title="Preferred Carrier Dispatch Requests"
                              :loading="loadingLoad">
          <template slot="toolbarButtons">
            <v-btn flat color="accent" @click="openAcceptDialog">
              <v-layout column>
                <v-icon>add_box</v-icon>
                Accept
              </v-layout>
            </v-btn>
          </template>
        </v-load-group-details>
      </template>
    </v-master-detail-pane>
  </v-layout>
</template>

<script>
import _ from 'lodash';
import API from '@/api';
import loadGroupMixin from '@/mixins/loadGroupMixin';
import dispatchRequestsMixin from '@/mixins/dispatchRequestsMixin';
import bookedLoadMixin from '@/mixins/bookedLoadMixin';

import companyNamesMixin from '@/mixins/companyNames';
import filterLoads from '@/mixins/filterLoads';
import refreshPageMixin from '@/mixins/refreshPageMixin';
import loadsRoutingMixin from '@/mixins/loadsRoutingMixin';

import LoadMap from '@/components/LoadMap';
import VMasterDetailPane from '@/components/MasterDetailPane';
import VFloatField from '@/components/VFloatField';
import VEditLoadDetails from '@/components/InfoWidgets/EditLoadDetails';

import VLoadNavList from '@/components/dashboard/simple/LoadNavList';
import VBooked from '@/pages/Companies/Loads/components/Booked';
import VLoadGroupDetails from './LoadGroupDetails';
import unitsOfMeasurePlugin from '../../../mixins/unitsOfMeasurePlugin';

export default {
  name: 'VDispatchedLoadRequests',
  components: {
    VLoadGroupDetails,
    VBooked,
    VLoadNavList,
    LoadMap,
    VEditLoadDetails,
    VFloatField,
    VMasterDetailPane,
  },
  mixins: [
    bookedLoadMixin,
    companyNamesMixin,
    filterLoads,
    dispatchRequestsMixin,
    loadGroupMixin,
    loadsRoutingMixin,
    refreshPageMixin,
    unitsOfMeasurePlugin,
  ],
  filters: {
    formatTimeStamp(momentDate) {
      return momentDate.format('HH:mm A MMM DD');
    },
  },
  watch: {
    dispatchRequests(newVal) {
      const haystack = newVal || [];
      const selectedId = _.get(this.selectedLoad, 'humanId') || this.dispatchRequestId;
      this.selectedLoad = _.find(haystack, dr => dr.humanId === selectedId);
    },
    selectedLoad(load) {
    /*  if (load) {
        const routeConfig = {
          name: loadsSectionRoute.dispatch_offers,
          // hash: this.$route.hash,  // don't retain the tab on purpose
          params: { id: this.company.id, dispatchRequestId: load.humanId },
        };
        this.$router.push(routeConfig);
      }
     */
    },
  },
  props: {
    company: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    showDispatchDialog: false,
    acceptedDispatchLoads: null,

    sortDesc: true,
    selectedLoad: null,
    tab: null,
    loadingBtn: false,
  }),
  computed: {
    _() {
      return _;
    },
    accurateSince() {
      if (this.loadingDispatchRequests) {
        return 'Loading ...';
      }

      return `Updated ${this.$options.filters.formatTimeStamp(this.dispatchRequestsTime)}`;
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },
    availableLoads() {
      if (!this.selectedLoad) {
        return null;
      }

      return this.dispatchRequestVisibleCount(this.selectedLoad);
    },
    loadsOptions() {
      if (!this.selectedLoad) {
        return null;
      }
      return this.dispatchRequestAcceptLoadsOptions(this.selectedLoad);
    },
    bookedContracts() {
      if (!this.selectedLoad) {
        return [];
      }

      const loadIds = _.chain(this.selectedLoad.contracts)
        .flatMap(c => c.bookedLoads)
        .map(l => l.humanId)
        .value();

      return _.filter(this.bookedLoads, l => _.find(loadIds, i => i === l.humanId));
    },
    filteredDispatchRequests() {
      return _.filter(this.dispatchRequests, dr => this.filterDispatchRequest(dr));
    },
    groupedByDispatcherLoads() {
      const nameSorted = _.sortBy(this.filteredDispatchRequests, load => _.get(load, 'dispatcher.companyName', ''));
      return _.groupBy(nameSorted, load => _.get(load, 'dispatcher.companyName', ''));
    },
  },
  methods: {
    reloadDispatchRequests() {
      this.loadDispatchedRequests(this.company.id);
    },
    loadData() {
      this.reloadDispatchRequests();
    },
    selectLoad(load) {
      this.selectedLoad = load;
    },
    isSelectedLoad(load) {
      return load.humanId === _.get(this.selectedLoad, 'humanId', null);
    },
    pricePerDistance(load) {
      const distance = this.loadGroupLoadedDistance(load);
      const rate = load.payableAmount;

      return parseFloat(rate/distance).toFixed(2);
    },
    openAcceptDialog() {
      this.showDispatchDialog = true;
    },
    onRefresh() {
      if (this.selectedLoad) {
        this.refreshLoad(this.selectedLoad, this.company.id);
      }
    },
    async acceptDispatchAssignment() {
      const isValid = await this.$validator.validateAll('modal');

      if (isValid) {
        try {
          this.loadingBtn = true;
          const load = {
            companyId: this.company.id,
            loadCount: this.acceptedDispatchLoads,
          };
          await API.dispatchers.acceptAssigment(this.selectedLoad.humanId, load);

          this.$store.dispatch('applications/setSuccessNotification', {
            message: 'Accepted',
            timeout: 7000,
          });

          this.showDispatchDialog = false;
          this.loadDispatchedRequests(this.company.id);
          this.loadBookedLoads(this.company.id);
        } catch (error) {
          this.$store.dispatch('applications/setErrorNotification', {
            message: error.message,
          });
        } finally {
          this.loadingBtn = false;
        }
      }
    },
  },
  created() {
    if (this.company.id !== this.dispatchRequestsCompanyId) {
      this.reloadDispatchRequests();
    }
  },
};
</script>

<style>

</style>



// WEBPACK FOOTER //
// src/components/dashboard/simple/DispatchedLoadRequests.vue